import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/Toast';
import { styles, colors, radius } from '../../styles/theme';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access_token')}` });

/**
 * Hızlı Puantaj Giriş Modalı
 * Lokasyon seç → o ay çalışan personeller otomatik satır → her biri için:
 * - Çal. Gün, Ek Mesai Saat, Avans, Not
 * - Saatlik ücret × saat × (ek mesai çarpanı) otomatik fazla mesai tutarı
 *   (çarpan İK Ayarlar config'inden gelir; default 1.0 = saat karşılığı)
 * - Tek tuşla toplu kaydet (upsert — mevcut puantaj varsa günceller, yoksa oluşturur)
 */
const HizliPuantajModal = ({ ay, onClose, onSaved, lokasyonlar, fazlaMesaiCarpani = 1.0 }) => {
  const toast = useToast();
  const [lokasyonId, setLokasyonId] = useState('');
  const [personeller, setPersoneller] = useState([]);
  const [mevcutPuantajlar, setMevcutPuantajlar] = useState({});
  const [satirlar, setSatirlar] = useState({}); // { personel_id: { calisma_gun, ek_mesai_saat, avans, notlar } }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mesaiCarpani = Number(fazlaMesaiCarpani) || 1.0; // Ek mesai çarpanı (İK Ayarlar config)

  const fetchData = useCallback(async () => {
    if (!lokasyonId) { setPersoneller([]); return; }
    setLoading(true);
    try {
      const [persRes, puaRes] = await Promise.all([
        fetch(`${API_URL}/api/personel?calisan_ay=${ay}&lokasyon_id=${lokasyonId}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/puantaj?ay=${ay}&lokasyon_id=${lokasyonId}`, { headers: authHeaders() }),
      ]);
      const pers = persRes.ok ? await persRes.json() : [];
      const pua = puaRes.ok ? await puaRes.json() : [];
      setPersoneller(pers);
      // Mevcut puantajları personel_id bazlı map'le
      const map = {};
      pua.forEach(p => { map[p.personel_id] = p; });
      setMevcutPuantajlar(map);
      // Satırları initialize et
      const initSatirlar = {};
      pers.forEach(p => {
        const existing = map[p.id];
        const calismaGun = existing?.calisma_gun ?? 30;
        // Faz F: gelinen_gun default 26 (calisma_gun 30 kalır) — düzenlenebilir
        const gelinenGun = existing?.gelinen_gun ?? 26;
        initSatirlar[p.id] = {
          calisma_gun: calismaGun,
          gelinen_gun: gelinenGun,
          ek_mesai_saat: existing?.fazla_mesai_saat ?? 0,
          avans: existing?.avans ?? 0,
          notlar: existing?.notlar || '',
          kilitli: existing && existing.durum !== 'TASLAK',
          net_maas: p.varsayilan_brut_maas || 0,
          gunluk_yol_ucreti: p.gunluk_yol_ucreti || 0,
          // Faz F: salt-okunur nihai önizleme için (kaydetme payload'ına GİRMEZ)
          varsayilan_resmi_odeme: p.varsayilan_resmi_odeme || 0,
          tip: p.tip || 'KADRO',
          // Marka bazlı günlük çalışma saati — saatlik ücret bazı (default 8)
          gunluk_calisma_saati: p.gunluk_calisma_saati || 8,
        };
      });
      setSatirlar(initSatirlar);
    } catch (e) {
      logError(e);
      toast.error('Yüklenemedi');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lokasyonId, ay]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateSatir = (pid, field, value) => {
    setSatirlar(prev => ({
      ...prev,
      [pid]: { ...prev[pid], [field]: value },
    }));
  };

  const saatlikUcret = (netMaas, gunlukSaat) => (netMaas || 0) / (30 * (Number(gunlukSaat) || 8));
  const mesaiTutari = (netMaas, saat, gunlukSaat) => Math.round(saatlikUcret(netMaas, gunlukSaat) * parseDecimal(saat) * mesaiCarpani * 100) / 100;
  // Faz C: yol = gelinen_gun × günlük yol ücreti (backend de aynı formülü uygular)
  const yolTutari = (gelinenGun, gunlukYol) => Math.round(parseDecimal(gelinenGun) * (gunlukYol || 0) * 100) / 100;

  const handleKaydet = async () => {
    if (!lokasyonId) { toast.warning('Lokasyon seç'); return; }
    const pidler = Object.keys(satirlar).filter(pid => !satirlar[pid].kilitli);
    if (!pidler.length) { toast.warning('Kaydedilecek satır yok'); return; }

    setSaving(true);
    let basarili = 0, hatali = 0;
    for (const pid of pidler) {
      const s = satirlar[pid];
      const mevcut = mevcutPuantajlar[pid];
      const fazlaTutar = mesaiTutari(s.net_maas, s.ek_mesai_saat, s.gunluk_calisma_saati);

      try {
        if (mevcut) {
          // Update
          const r = await fetch(`${API_URL}/api/puantaj/${mevcut.id}`, {
            method: 'PUT',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              calisma_gun: parseDecimal(s.calisma_gun) || 30,
              gelinen_gun: (s.gelinen_gun === '' || s.gelinen_gun == null) ? null : parseDecimal(s.gelinen_gun),
              avans: parseDecimal(s.avans) || 0,
              fazla_mesai_saat: parseDecimal(s.ek_mesai_saat) || 0,
              fazla_mesai_tutar: fazlaTutar,
              notlar: s.notlar || null,
            }),
          });
          if (r.ok) basarili++;
          else hatali++;
        } else {
          // Create
          const r = await fetch(`${API_URL}/api/puantaj`, {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              personel_id: parseInt(pid, 10),
              ay: `${ay}-01`,
              lokasyon_id: parseInt(lokasyonId, 10),
              calisma_gun: parseDecimal(s.calisma_gun) || 30,
              gelinen_gun: (s.gelinen_gun === '' || s.gelinen_gun == null) ? null : parseDecimal(s.gelinen_gun),
              aylik_brut_maas: s.net_maas,
              // yol_yardimi gönderilmiyor → backend gelinen_gun × günlük ücret olarak türetir
              avans: parseDecimal(s.avans) || 0,
              fazla_mesai_saat: parseDecimal(s.ek_mesai_saat) || 0,
              fazla_mesai_tutar: fazlaTutar,
              notlar: s.notlar || null,
            }),
          });
          if (r.ok) basarili++;
          else hatali++;
        }
      } catch {
        hatali++;
      }
    }

    setSaving(false);
    if (basarili > 0) toast.success(`${basarili} puantaj kaydedildi${hatali ? `, ${hatali} hata` : ''}`);
    if (hatali > 0 && basarili === 0) toast.error('Kayıt başarısız');
    onSaved();
  };

  const fmt = (n) => (n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

  const tumSayisi = Object.keys(satirlar).length;
  const kilitli = Object.values(satirlar).filter(s => s.kilitli).length;
  const taslak = tumSayisi - kilitli;

  const input = { width: '100%', padding: '5px 6px', border: `1px solid ${colors.border}`, borderRadius: 3, fontSize: 12, boxSizing: 'border-box', fontFamily: 'monospace' };
  const inputSmall = { ...input, fontSize: 11 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', padding: 20, borderRadius: radius.lg, width: '95vw', maxWidth: 1100, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>⚡ Hızlı Puantaj Giriş · {ay}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#999' }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Lokasyon:</label>
          <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 13, minWidth: 200 }}>
            <option value="">(Seçiniz)</option>
            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
          </select>
          {lokasyonId && (
            <span style={{ fontSize: 12, color: '#666' }}>
              {personeller.length} personel (bu ay çalışan) · {taslak} düzenlenebilir{kilitli > 0 && ` · ${kilitli} kapalı`}
            </span>
          )}
        </div>

        {!lokasyonId ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Hızlı giriş için lokasyon seçin.</div>
        ) : loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
        ) : personeller.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Bu lokasyonda {ay} ayında çalışan personel yok.</div>
        ) : (
          <>
            <div style={{ background: '#fffde7', border: '1px solid #fff59d', padding: 10, borderRadius: 4, fontSize: 11, color: '#f57f17', marginBottom: 10 }}>
              💡 Çalışma Gün default 30, Gelinen Gün default 26. Ek mesai saati girersen → saatlik ücret × saat × {mesaiCarpani} = fazla mesai tutarı otomatik hesaplanır. Hepsini bir kere "Kaydet"e bas.
              <div style={{ marginTop: 4, color: '#9e7b00' }}>
                ≈ <strong>Maaş / Resmi / Nihai</strong> sütunları <strong>tahmini önizlemedir</strong> (tarayıcıda hesaplanır). Kesin tutarlar kaydederken backend tarafından hesaplanır.
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={th}>Personel</th>
                    <th style={thR}>Net Maaş</th>
                    <th style={thR}>Çal. Gün</th>
                    <th style={thR} title="İşe gelinen gün. Yol = Gelinen Gün × günlük yol ücreti.">Gelinen Gün</th>
                    <th style={thR}>Ek Mesai (saat)</th>
                    <th style={thR}>Avans (₺)</th>
                    <th style={th}>Not</th>
                    <th style={{ ...thR, background: '#eef4ff', borderLeft: '2px solid #c7d8f5' }} title="Maaş ≈ Net Maaş × Çal. Gün / 30 (tahmini önizleme)">≈ Maaş (₺)</th>
                    <th style={{ ...thR, background: '#eef4ff' }} title="Yol = Gelinen Gün × günlük yol ücreti">≈ Yol (₺)</th>
                    <th style={{ ...thR, background: '#eef4ff' }} title={`Ek Mesai tutarı = saatlik ücret × saat × ${mesaiCarpani}`}>≈ Mesai (₺)</th>
                    <th style={{ ...thR, background: '#eef4ff' }} title="Resmi = sadece KADRO için varsayılan resmi ödeme × min(Çal. Gün, 30) / 30">≈ Resmi (₺)</th>
                    <th style={{ ...thR, background: '#eef4ff', borderRight: '2px solid #c7d8f5' }} title="Nihai ≈ (Maaş + Yol + Mesai) − Resmi − Avans (tahmini; kesin değer kayıtta backend'de)">≈ Nihai (₺)</th>
                    <th style={th}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {personeller.map(p => {
                    const s = satirlar[p.id] || {};
                    const mevcut = mevcutPuantajlar[p.id];
                    const fazlaTl = mesaiTutari(s.net_maas, s.ek_mesai_saat, s.gunluk_calisma_saati);
                    const yolTl = yolTutari(s.gelinen_gun, s.gunluk_yol_ucreti);
                    // Faz F: salt-okunur tahmini önizleme (kaydetme payload'ını ETKİLEMEZ)
                    const calismaGunNum = parseDecimal(s.calisma_gun);
                    const maasTl = Math.round((s.net_maas || 0) * calismaGunNum / 30 * 100) / 100;
                    const resmiTl = s.tip === 'KADRO'
                      ? Math.round((s.varsayilan_resmi_odeme || 0) * Math.min(calismaGunNum, 30) / 30 * 100) / 100
                      : 0;
                    const avansTl = parseDecimal(s.avans);
                    const nihaiTl = Math.round((maasTl + yolTl + fazlaTl - resmiTl - avansTl) * 100) / 100;
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', background: s.kilitli ? '#fafafa' : 'white' }}>
                        <td style={td}>
                          <strong>{p.ad_soyad}</strong>
                          <div style={{ fontSize: 10, color: '#888' }}>{p.pozisyon || '-'}</div>
                        </td>
                        <td style={tdR}>{fmt(s.net_maas)} ₺</td>
                        <td style={tdR}>
                          <input style={{ ...inputSmall, width: 55, textAlign: 'right' }} type="text" inputMode="decimal" disabled={s.kilitli}
                            value={s.calisma_gun} onChange={e => updateSatir(p.id, 'calisma_gun', normalizeDecimalInput(e.target.value))} />
                        </td>
                        <td style={tdR}>
                          <input style={{ ...inputSmall, width: 55, textAlign: 'right' }} type="text" inputMode="decimal" disabled={s.kilitli}
                            value={s.gelinen_gun} onChange={e => updateSatir(p.id, 'gelinen_gun', normalizeDecimalInput(e.target.value))} />
                        </td>
                        <td style={tdR}>
                          <input style={{ ...inputSmall, width: 55, textAlign: 'right' }} type="text" inputMode="decimal" disabled={s.kilitli}
                            value={s.ek_mesai_saat} onChange={e => updateSatir(p.id, 'ek_mesai_saat', normalizeDecimalInput(e.target.value))} placeholder="0" />
                        </td>
                        <td style={tdR}>
                          <input style={{ ...inputSmall, width: 80, textAlign: 'right' }} type="text" inputMode="decimal" disabled={s.kilitli}
                            value={s.avans} onChange={e => updateSatir(p.id, 'avans', normalizeDecimalInput(e.target.value))} placeholder="0" />
                        </td>
                        <td style={td}>
                          <input style={{ ...inputSmall, width: '100%' }} disabled={s.kilitli}
                            value={s.notlar} onChange={e => updateSatir(p.id, 'notlar', e.target.value)} placeholder="-" />
                        </td>
                        <td style={{ ...tdR, background: '#f7faff', borderLeft: '2px solid #e0eafc' }}>{fmt(maasTl)} ₺</td>
                        <td style={{ ...tdR, background: '#f7faff', color: '#2e7d32', fontWeight: 600 }} title={`${s.gunluk_yol_ucreti || 0} ₺/gün × ${s.gelinen_gun || 0} gün`}>{fmt(yolTl)} ₺</td>
                        <td style={{ ...tdR, background: '#f7faff', color: '#1565c0', fontWeight: 600 }}>{fmt(fazlaTl)} ₺</td>
                        <td style={{ ...tdR, background: '#f7faff', color: s.tip === 'KADRO' ? '#c62828' : '#bbb' }} title={s.tip === 'KADRO' ? 'KADRO — resmi ödeme' : `${s.tip || 'KADRO'} — resmi ödeme yok`}>
                          {s.tip === 'KADRO' ? `${fmt(resmiTl)} ₺` : '—'}
                        </td>
                        <td style={{ ...tdR, background: '#f7faff', fontWeight: 700, borderRight: '2px solid #e0eafc' }}>{fmt(nihaiTl)} ₺</td>
                        <td style={td}>
                          {mevcut ? (
                            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: mevcut.durum === 'TASLAK' ? '#fff3e0' : '#e8f5e9', color: mevcut.durum === 'TASLAK' ? '#e65100' : '#2e7d32' }}>{mevcut.durum}</span>
                          ) : (
                            <span style={{ fontSize: 10, color: '#999' }}>(yeni)</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '9px 18px', background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>İptal</button>
              <button onClick={handleKaydet} disabled={saving || taslak === 0}
                style={{ padding: '9px 24px', background: styles.success, color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14 }}>
                {saving ? 'Kaydediliyor...' : `✓ ${taslak} Satırı Toplu Kaydet`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const th = { padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555', borderBottom: '2px solid #ddd' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '5px 8px', fontSize: 11 };
const tdR = { ...td, textAlign: 'right', fontFamily: 'monospace' };

export default HizliPuantajModal;
